import {
    ReactElement,
    useEffect,
    createContext,
    use,
    useState,
    useCallback,
    useId,
    useMemo,
    Fragment,
    PropsWithChildren,
    Children,
} from "react";

type IPageTitleContext = {
    title?: string;
    setTitle: (newTitle?: string) => void;
    buttons: React.ReactNode[];
    registerButtons: (
        id: string,
        newButtons: React.ReactNode,
        order?: number,
    ) => void;
    unregisterButtons: (id: string) => void;
    badges: React.ReactNode[];
    registerBadges: (
        id: string,
        newBadges: React.ReactNode,
        order?: number,
    ) => void;
    unregisterBadges: (id: string) => void;
    visible: boolean;
    setVisible: (visible: boolean) => void;
    additionalContent: React.ReactNode;
    setAdditionalContent: (additionalContent: React.ReactNode) => void;
};

/**
 * A registered group of header nodes (buttons or badges). Groups are rendered
 * in ascending `order` (higher numbers render last, z-index style); ties fall
 * back to the registration id, which is stable for a given component tree.
 */
type HeaderGroup = { node: React.ReactNode; order: number };

const sortGroups = (groups: Record<string, HeaderGroup>): React.ReactNode[] =>
    Object.entries(groups)
        .sort(
            ([idA, a], [idB, b]) => a.order - b.order || idA.localeCompare(idB),
        )
        .map(([id, { node }]) => <Fragment key={id}>{node}</Fragment>);

const PageTitleContext = createContext<IPageTitleContext>({
    title: "Illuminate",
    setTitle: (title?: string) => {
        console.warn(
            "No context setTitle handler has been set.\r\nNew page title: ",
            title,
        );
    },
    buttons: [],
    registerButtons: () => {
        console.warn("No context registerButtons handler has been set.");
    },
    unregisterButtons: () => {
        console.warn("No context unregisterButtons handler has been set.");
    },
    badges: [],
    registerBadges: () => {
        console.warn("No context registerBadges handler has been set.");
    },
    unregisterBadges: () => {
        console.warn("No context unregisterBadges handler has been set.");
    },
    visible: true,
    setVisible: (visible: boolean) => {
        console.warn(
            "No context setVisible handler has been set.\r\nNew page visibility: ",
            visible,
        );
    },
    additionalContent: undefined,
    setAdditionalContent: () => {
        console.warn("No context setAdditionalContent handler has been set.");
    },
});

export const usePageTitle = () => {
    const context = use(PageTitleContext);
    if (!context) {
        throw new Error(
            "usePageTitle must be used within an PageTitleProvider",
        );
    }
    return context;
};

/**
 * Page title context provider.
 *
 * Dependencies: This provider requires a HeltmetProvider parent.
 * @param options
 * @returns
 */
export const PageTitleProvider = ({ children }: PropsWithChildren) => {
    const [title, setTitle] = useState<string | undefined>();
    const [buttonGroups, setButtonGroups] = useState<
        Record<string, HeaderGroup>
    >({});
    const [badgeGroups, setBadgeGroups] = useState<Record<string, HeaderGroup>>(
        {},
    );
    const [visible, setVisible] = useState<boolean>(true);
    const [additionalContent, setAdditionalContent] =
        useState<React.ReactNode>();

    const registerButtons = useCallback(
        (id: string, newButtons: React.ReactNode, order = 0) => {
            setButtonGroups((previous) => ({
                ...previous,
                [id]: { node: newButtons, order },
            }));
        },
        [],
    );
    const unregisterButtons = useCallback((id: string) => {
        setButtonGroups((previous) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [id]: _removed, ...rest } = previous;
            return rest;
        });
    }, []);
    const registerBadges = useCallback(
        (id: string, newBadges: React.ReactNode, order = 0) => {
            setBadgeGroups((previous) => ({
                ...previous,
                [id]: { node: newBadges, order },
            }));
        },
        [],
    );
    const unregisterBadges = useCallback((id: string) => {
        setBadgeGroups((previous) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [id]: _removed, ...rest } = previous;
            return rest;
        });
    }, []);

    const buttons = useMemo(() => sortGroups(buttonGroups), [buttonGroups]);
    const badges = useMemo(() => sortGroups(badgeGroups), [badgeGroups]);

    const value = {
        title,
        setTitle,
        buttons,
        registerButtons,
        unregisterButtons,
        badges,
        registerBadges,
        unregisterBadges,
        visible,
        setVisible,
        additionalContent,
        setAdditionalContent,
    };

    return <PageTitleContext value={value}>{children}</PageTitleContext>;
};

export const PageTitle = ({
    title,
    visible = true,
    additionalContent,
}: {
    title?: string;
    visible?: boolean;
    additionalContent?: React.ReactNode;
}): ReactElement => {
    const windowTitle = title ? `Illuminate - ${title}` : "Illuminate";
    const { setTitle, setVisible } = usePageTitle();

    useEffect(() => {
        setTitle(title);
        setVisible(visible);
        return () => {
            setTitle(undefined);
            setVisible(false);
        };
    }, [title, setTitle, setVisible, visible, additionalContent]);

    return <title>{windowTitle}</title>;
};

export const ActionButtons = ({
    children,
    order,
}: PropsWithChildren<{ order?: number }>) => {
    const id = useId();
    const { registerButtons, unregisterButtons } = usePageTitle();

    useEffect(() => {
        registerButtons(id, Children.toArray(children), order);
        return () => unregisterButtons(id);
    }, [id, children, order, registerButtons, unregisterButtons]);

    return null;
};

export const HeaderBadges = ({
    children,
    order,
}: PropsWithChildren<{ order?: number }>) => {
    const id = useId();
    const { registerBadges, unregisterBadges } = usePageTitle();

    useEffect(() => {
        registerBadges(id, Children.toArray(children), order);
        return () => unregisterBadges(id);
    }, [id, children, order, registerBadges, unregisterBadges]);

    return null;
};

export const AdditionalContent = ({ children }: PropsWithChildren) => {
    const { setAdditionalContent } = usePageTitle();

    useEffect(() => {
        setAdditionalContent(children);
        return () => setAdditionalContent(undefined);
    }, [children, setAdditionalContent]);

    return null;
};
